import { AxiosResponse } from 'axios';
import { LdJsonBreadCrumbsResort } from 'components/LdJson';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React, { useMemo } from 'react';
import { Routes } from 'routes';

import { getResortSlug } from 'util/memcache';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortAccommodations,
  fetchResortRelated, fetchResortRelatedNews, fetchResortShortInfo, fetchResortShortWeatherReport, fetchSnowfallInfo,
} from 'api/resort/static';
import {
  HistoricalSnowfallType,
  IRelatedNews,
  IResortPageInfo,
  IResortPathInfo,
  ISnowfallItem,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { HistoricalSnowfall } from 'components/resort/HistoricalSnowfall';
import { BlockContent } from 'components/blocks/BlockContent';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import { RegularAd } from 'googlead/components/RegularAd';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { IAccommodationPage } from 'api/tripadvisor/types';

export type ResortHistoricalSnowfallPageProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
  snowfallInfoAnnual: Array<ISnowfallItem> | null
  relatedNews: IRelatedNews | null,
  accommodations: IAccommodationPage | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-historical-snowfall-page';
export const TEMPLATE_ID = '3.2.5';
export const TEMPLATE_NAME = 'Resort Historical Snowfall';

export default function ResortHistoricalSnowfallPage({
  resort,
  pathInfo,
  snowfallInfoMonthly,
  snowfallInfoAnnual,
  relatedNews,
}: ResortHistoricalSnowfallPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  type KeyAverage = 'totalSnow' | 'snowDays' | 'baseDepth' | 'maxBaseDepth' | 'biggestSnowfall';
  const setAverageInstance = (property: KeyAverage) => {
    const sum = (snowfallInfoAnnual || []).reduce((prev, item) => prev + item[property], 0);
    return Math.ceil(sum / (snowfallInfoAnnual?.length || 1));
  };

  const averages = useMemo(() => ({
    date: t('resort.historicalSnowfall.average'),
    totalSnow: setAverageInstance('totalSnow'),
    snowDays: setAverageInstance('snowDays'),
    baseDepth: setAverageInstance('baseDepth'),
    maxBaseDepth: setAverageInstance('maxBaseDepth'),
    biggestSnowfall: setAverageInstance('biggestSnowfall'),
  }), [snowfallInfoAnnual]);

  const maxBiggestSnowfall = Math.max(0, ...(snowfallInfoAnnual ?? []).map((v) => v.biggestSnowfall));

  if (!resort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.historicalSnowfall.seoTitle', { resort: resort.title })} />
          <BlockContent>
            {m('resort.historicalSnowfall.seoText', {
              resort: resort.title,
              avgsnowfall: dt(averages.totalSnow),
              avgsnowfalldays: `${averages.snowDays ?? ''}`,
              bgstsnowfall: dt(maxBiggestSnowfall),
            })}
          </BlockContent>
        </BoxWithShadow>
        {snowfallInfoAnnual
          && snowfallInfoMonthly
          && (!!(snowfallInfoMonthly.length) || !!(snowfallInfoAnnual.length)) && (
            <HistoricalSnowfall
              snowfallInfoMonthly={snowfallInfoMonthly}
              snowfallInfoAnnual={snowfallInfoAnnual}
              resortName={resort.title}
            />
        )}
        <PlanYourTrip
          hasSkiRental={pathInfo.hasSkiRental}
          hasFlights={pathInfo.hasFlights}
          hasCarRental={pathInfo.hasCarRental}
          resort={pathInfo}
          liftsUrl={resort.liftsUrl}
          rentalUrl={resort.rentalUrl}
          lessonsUrl={resort.lessonsUrl}
          isPartner={resort.isPartner}
          resortCountry={resort.region?.country_code}
        />
        <RegularAd isFirst />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={resort.region}
        fn={Routes.resortSnowfall}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortHistoricalSnowfallPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const snowfallInfoMonthly = await fetchSnowfallInfo(uuid, HistoricalSnowfallType.monthly);
  const snowfallInfoAnnual = await fetchSnowfallInfo(uuid, HistoricalSnowfallType.annual);
  const relatedNews = await fetchResortRelatedNews(uuid);

  return {
    type: 'resort',
    route: 'historical-snowfall',
    pathInfo: cache.data,
    resort: await fetchResortShortInfo(uuid),
    shortWeather: await fetchResortShortWeatherReport(uuid),
    snowfallInfoMonthly,
    snowfallInfoAnnual,
    relatedNews,

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.SNOWPLANNER,
      tab: CurrentTab.TRIP_PLANNING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort historical',
    hero: {
      title: 'resort.historicalSnowfall.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.historical.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.historical.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),

  };
};

// This function gets called at build time
export const getResortHistoricalSnowfallPageStaticProps = wrapStaticProps(fetchProps);
export const getResortHistoricalSnowfallPageServerSideProps = wrapServerSideProps(fetchProps);
